import React, { useState } from 'react';
import { X, Search, Wifi, CheckCircle, XCircle, AlertTriangle, Loader, Server, Shield, Monitor, Zap, Network, Eye, EyeOff } from 'lucide-react';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { canTcpProbe } from '../utils/deviceProbe';

interface DeviceCapabilities {
  max_persons: number;
  max_face_templates: number;
  supports_liveness: boolean;
  supports_anti_spoofing: boolean;
  supports_temperature: boolean;
  supports_mask_detection: boolean;
  has_display: boolean;
  has_speaker: boolean;
  has_relay: boolean;
  has_camera: boolean;
  supports_multiple_cards: boolean;
  supports_wiegand: boolean;
}

interface DeviceInfo {
  device_name: string;
  model: string;
  serial_number: string;
  firmware_version: string;
  hardware_version: string;
  vendor: string;
  mac_address: string;
  ip_address: string;
  local_ip_address?: string;
  device_type: string; // 'face_recognition' or 'tag_reader'
  capabilities: DeviceCapabilities;
}

interface ProbeResult {
  device_info?: DeviceInfo;
  vendor: string;
  confidence: number;
  is_compatible: boolean;
  compatibility_msg: string;
  detection_time_ms: number;
  error?: string;
}

interface DeviceCredentials {
  username: string;
  password: string;
}

interface DeviceProbeProps {
  show: boolean;
  onClose: () => void;
  onDeviceDetected: (deviceInfo: DeviceInfo, credentials?: DeviceCredentials) => void;
  initialIpAddress?: string;
}

const DeviceProbe: React.FC<DeviceProbeProps> = ({
  show,
  onClose,
  onDeviceDetected,
  initialIpAddress = ''
}) => {
  const [ipAddress, setIpAddress] = useState(initialIpAddress);
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [probing, setProbing] = useState(false);
  const [localProbing, setLocalProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);

  const tryLocalProbe = async (ip: string, user: string, pass: string): Promise<ProbeResult | null> => {
    if (!canTcpProbe()) return null;
    try {
      const r = await (window as any).electronAPI.deviceHttpRequest({
        url: `http://${ip}/ISAPI/System/deviceInfo`,
        method: 'GET',
        digestAuth: { username: user || 'admin', password: pass || '' },
        timeout: 5000,
      });
      if (!r.success || !r.data) return null;

      const xml = new DOMParser().parseFromString(r.data, 'text/xml');
      const get = (tag: string) => xml.getElementsByTagName(tag)[0]?.textContent ?? '';
      const model = get('model');
      if (!model) return null;

      return {
        device_info: {
          device_name: get('deviceName') || `Hikvision ${model}`,
          model,
          serial_number: get('serialNumber'),
          firmware_version: get('firmwareVersion'),
          hardware_version: get('hardwareVersion'),
          vendor: 'hikvision',
          mac_address: get('macAddress'),
          ip_address: ip,
          local_ip_address: ip,
          device_type: 'facial_reader',
          capabilities: null,
        },
        vendor: 'hikvision',
        confidence: 90,
        is_compatible: true,
        compatibility_msg: `Hikvision ${model} detectado localmente`,
        detection_time_ms: r.timeMs ?? 0,
      };
    } catch {
      return null;
    }
  };

  const handleProbe = async () => {
    if (!ipAddress.trim()) {
      toast.error('Por favor, insira um endereço IP');
      return;
    }

    setProbing(true);
    setProbeResult(null);

    try {
      // Ensure protocol is included, default to http:// if not specified
      let deviceUrl = ipAddress.trim();
      if (!deviceUrl.match(/^https?:\/\//)) {
        deviceUrl = `http://${deviceUrl}`;
      }

      // Electron: try local probe first
      if (canTcpProbe()) {
        setLocalProbing(true);
        const localResult = await tryLocalProbe(ipAddress.trim().replace(/^https?:\/\//, ''), username.trim(), password);
        setLocalProbing(false);
        if (localResult) {
          setProbeResult(localResult);
          toast.success(`Dispositivo ${localResult.vendor} detectado localmente!`);
          return;
        }
      }

      const response = await api.post('/devices/probe', {
        ip_address: deviceUrl,
        username: username.trim() || undefined,
        password: password || undefined,
      });

      setProbeResult(response.data);

      if (response.data.is_compatible) {
        toast.success(`Dispositivo ${response.data.vendor} detectado com sucesso!`);
      } else {
        toast.error('Dispositivo não compatível ou não detectado');
      }
    } catch (error: any) {
      console.error('Probe error:', error);
      toast.error('Erro ao detectar dispositivo: ' + (error.response?.data?.error || error.message));

      // Show error result
      setProbeResult({
        vendor: '',
        confidence: 0,
        is_compatible: false,
        compatibility_msg: 'Falha na detecção: ' + (error.response?.data?.error || error.message),
        detection_time_ms: 0,
        error: error.response?.data?.error || error.message,
      });
    } finally {
      setLocalProbing(false);
      setProbing(false);
    }
  };

  const handleUseDevice = () => {
    if (probeResult?.device_info) {
      // Pass credentials along with device info if password was entered
      const credentials = password ? { username, password } : undefined;

      // Populate both cloud and local IP with the detected IP by default
      // User can modify either field afterward if they want different IPs
      const deviceInfoWithLocalIp = {
        ...probeResult.device_info,
        local_ip_address: probeResult.device_info.ip_address
      };

      onDeviceDetected(deviceInfoWithLocalIp, credentials);
      onClose();
    }
  };

  const handleReset = () => {
    setProbeResult(null);
    setIpAddress('');
    setUsername('admin');
    setPassword('');
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Network className="h-6 w-6" />
                Detectar Dispositivo
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Digite o endereço IP para detectar automaticamente o fabricante e modelo
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* IP Address Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço IP do Dispositivo
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !probing && handleProbe()}
                  placeholder="Ex: 192.168.1.100"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  disabled={probing}
                />
                <button
                  type="button"
                  onClick={handleProbe}
                  disabled={probing || !ipAddress.trim()}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {probing ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      Detectando...
                    </>
                  ) : (
                    <>
                      <Search className="h-5 w-5" />
                      Detectar
                    </>
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-gray-500">
                  A detecção pode levar até 30 segundos
                </p>
                {localProbing && (
                  <span className="text-xs text-blue-500">Tentando localmente...</span>
                )}
              </div>
            </div>

            {/* Credentials Section */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuário
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  disabled={probing}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Opcional - para dispositivos que exigem autenticação
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !probing && handleProbe()}
                    placeholder="••••••••"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    disabled={probing}
                  />
                  {password && (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Opcional - senha do dispositivo
                </p>
              </div>
            </div>

            {/* Probe Result */}
            {probeResult && (
              <div className="mt-6 space-y-4">
                {/* Status Banner */}
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  probeResult.is_compatible
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {probeResult.is_compatible ? (
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      probeResult.is_compatible ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {probeResult.is_compatible ? 'Dispositivo Compatível' : 'Dispositivo Não Compatível'}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      probeResult.is_compatible ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {probeResult.compatibility_msg}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Tempo de detecção: {probeResult.detection_time_ms}ms | Confiança: {probeResult.confidence}%
                    </p>
                  </div>
                </div>

                {/* Device Details */}
                {probeResult.device_info && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Server className="h-5 w-5" />
                      Informações do Dispositivo
                    </h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Fabricante</p>
                        <p className="font-medium text-gray-900">{probeResult.device_info.vendor}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Modelo</p>
                        <p className="font-medium text-gray-900">{probeResult.device_info.model}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Número de Série</p>
                        <p className="font-medium text-gray-900">{probeResult.device_info.serial_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Versão do Firmware</p>
                        <p className="font-medium text-gray-900">{probeResult.device_info.firmware_version || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Endereço MAC</p>
                        <p className="font-medium text-gray-900">{probeResult.device_info.mac_address || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Endereço IP</p>
                        <p className="font-medium text-gray-900">{probeResult.device_info.ip_address}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Tipo de Dispositivo</p>
                        <p className="font-medium text-gray-900">
                          {probeResult.device_info.device_type === 'facial_reader' ? 'Leitor Facial' :
                           probeResult.device_info.device_type === 'rfid_reader' ? 'Leitor RFID' :
                           'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Capabilities */}
                    {probeResult.device_info.capabilities && (
                      <div className="pt-4 border-t border-gray-200">
                        <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Capacidades do Dispositivo
                        </h5>
                        <div className="grid grid-cols-2 gap-2">
                          {probeResult.device_info.capabilities.supports_liveness && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Detecção de Vivacidade</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.supports_anti_spoofing && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Anti-Falsificação</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.supports_temperature && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Medição de Temperatura</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.supports_mask_detection && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Detecção de Máscara</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.has_camera && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Câmera</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.has_display && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Display</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.has_speaker && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Alto-falante</span>
                            </div>
                          )}
                          {probeResult.device_info.capabilities.has_relay && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Relé de Porta</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                          <p>Capacidade: {probeResult.device_info.capabilities.max_persons.toLocaleString()} pessoas / {probeResult.device_info.capabilities.max_face_templates.toLocaleString()} faces</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          {probeResult && (
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Detectar Outro
            </button>
          )}
          {probeResult?.is_compatible && (
            <button
              type="button"
              onClick={handleUseDevice}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
            >
              <CheckCircle className="h-5 w-5" />
              Usar Este Dispositivo
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceProbe;
